/**
 * Route-level error boundary component
 * Provides route-specific error handling with navigation fallbacks
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { generateTempId } from '../../utils/uuid';
import { AlertTriangle, Home, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { reportErrorFromBoundary } from '../../monitoring/errorReporting';
import { isChunkLoadError } from '../../utils/isChunkLoadError';
import { recoverFromStaleChunk } from '../../utils/staleChunkRecovery';
import { errorLogger } from '../../utils/loggerInstance';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  routeName?: string;
  /** Used only when user clicks "Go Back" and there's no history. Optional: defaults to /chat. Never use the current route (boundary guards against loops). */
  fallbackRoute?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string;
  retryCount: number;
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public state: RouteErrorBoundaryState = {
    hasError: false,
    error: null,
    errorId: '',
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    const errorId = generateTempId('route_error');

    return {
      hasError: true,
      error,
      errorId,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, routeName } = this.props;

    errorLogger.error('Route error boundary caught an error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      routeName,
      url: globalThis.location.href,
    });

    reportErrorFromBoundary(error, {
      boundary: 'route',
      routeName,
      errorId: this.state.errorId,
      componentStack: errorInfo.componentStack,
    });

    if (onError) {
      onError(error, errorInfo);
    }

    // A missing hashed chunk cannot be fixed by remounting — only a reload
    // picks up the new index.html. Remount retry is for transient network faults.
    recoverFromStaleChunk(error);
    if (this.shouldAutoRetry(error)) {
      this.scheduleRetry();
    }
  }

  public componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private readonly shouldAutoRetry = (error: Error): boolean => {
    const { retryCount } = this.state;

    // Don't auto-retry more than twice
    if (retryCount >= 2) return false;

    const errorMessage = (error.message || '').toLowerCase();
    if (isChunkLoadError(error)) {
      return false;
    }
    return errorMessage.includes('network') || errorMessage.includes('fetch');
  };

  private readonly scheduleRetry = () => {
    const { retryCount } = this.state;
    const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s

    this.retryTimeoutId = globalThis.setTimeout(() => {
      this.handleRetry();
    }, delay);
  };

  private readonly handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorId: '',
      retryCount: prevState.retryCount + 1,
    }));

    errorLogger.info('Route error boundary retrying', {
      retryCount: this.state.retryCount + 1,
      routeName: this.props.routeName,
    });
  };

  /** Safe fallback when there's no history: never redirect to current path (avoids loops). */
  private readonly getSafeFallbackRoute = (): string => {
    const candidate = this.props.fallbackRoute || '/chat';
    const current = globalThis.location.pathname;
    if (candidate === current) return '/profile';
    return candidate;
  };

  private readonly handleGoBack = () => {
    if (globalThis.history.length > 1) {
      globalThis.history.back();
    } else {
      globalThis.location.href = this.getSafeFallbackRoute();
    }
  };

  private readonly handleGoHome = () => {
    globalThis.location.href = '/chat';
  };

  private readonly handleReload = () => {
    globalThis.location.reload();
  };

  public render() {
    const { hasError, error, retryCount } = this.state;
    const { children, routeName } = this.props;

    if (hasError) {
      const canRetry = retryCount < 3;
      const isChunkError = error != null && isChunkLoadError(error);

      let bodyMessage: string;
      if (isChunkError) {
        bodyMessage = 'The application has been updated. Please refresh to get the latest version.';
      } else if (routeName) {
        bodyMessage = 'Something went wrong on the ' + routeName + ' page.';
      } else {
        bodyMessage = 'Something went wrong loading this page.';
      }

      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center px-4'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center'>
            <div className='flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full'>
              <AlertTriangle className='w-8 h-8 text-red-600' />
            </div>

            <h1 className='text-xl font-semibold text-gray-900 mb-2'>
              {isChunkError ? 'Update Required' : 'Page Error'}
            </h1>

            <p className='text-gray-600 mb-6'>{bodyMessage}</p>

            <div className='flex flex-col gap-3 mb-4'>
              {isChunkError ? (
                <Button onClick={this.handleReload} className='w-full'>
                  <RefreshCw className='w-4 h-4 mr-2' />
                  Refresh Page
                </Button>
              ) : (
                <>
                  {canRetry && (
                    <Button onClick={this.handleRetry} className='w-full'>
                      <RefreshCw className='w-4 h-4 mr-2' />
                      Try Again ({3 - retryCount} left)
                    </Button>
                  )}

                  <Button onClick={this.handleGoBack} variant='outline' className='w-full'>
                    <ArrowLeft className='w-4 h-4 mr-2' />
                    Go Back
                  </Button>

                  <Button onClick={this.handleGoHome} variant='outline' className='w-full'>
                    <Home className='w-4 h-4 mr-2' />
                    Go to Chat
                  </Button>
                </>
              )}
            </div>

            {process.env.NODE_ENV === 'development' && error && (
              <details className='text-left'>
                <summary className='cursor-pointer text-sm text-gray-600 hover:text-gray-800 mb-2'>
                  Error details (development only)
                </summary>
                <pre className='p-3 bg-gray-100 border border-gray-200 rounded text-xs overflow-auto max-h-32'>
                  {error.stack}
                </pre>
              </details>
            )}

            <p className='text-xs text-gray-500 mt-4'>Error ID: {this.state.errorId}</p>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default RouteErrorBoundary;
